"use client";

import Image from "next/image";
import { LogOut, User } from "lucide-react";
import { useAtomValue, useSetAtom } from "jotai";
import { removeStoredToken, userAtom, isLoggingOutAtom, authStatusAtom } from "@/store/useStore";
import { useRouter } from "next/navigation";

export default function Header() {
    const user = useAtomValue(userAtom);
    const setUser = useSetAtom(userAtom);
    const setIsLoggingOut = useSetAtom(isLoggingOutAtom);
    const setStatus = useSetAtom(authStatusAtom);
    const router = useRouter();

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);
            const { auth } = await import("@/lib/firebase");
            await auth.signOut();
            removeStoredToken();
            setUser(null);
            setStatus('unauthenticated');
            router.push("/login");
        } catch (error) {
            console.error("Logout error:", error);
            removeStoredToken();
            setUser(null);
            setStatus('unauthenticated');
            router.push("/login");
            setIsLoggingOut(false);
        }
    };

    return (
        <header className="h-20 bg-white border-b border-gray-100 flex items-center px-8 justify-between shadow-sm sticky top-0 z-50">
            <div className="flex items-center gap-4">
                <Image
                    src="/carry-ok-logo.png"
                    alt="Logo"
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="h-10 w-auto object-contain"
                    priority
                />
                <div>
                    <h1 className="text-xl font-bold text-slate-900 tracking-tight">Carry OK</h1>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold -mt-1">Admin Portal</p>
                </div>
            </div>

            <div className="flex items-center gap-3">
                {user && (
                    <div className="flex items-center gap-4 p-1.5 rounded-2xl border border-slate-100 bg-slate-50/50">
                        {user.photoURL ? (
                            <Image
                                src={user.photoURL}
                                alt={user.displayName}
                                width={36}
                                height={36}
                                className="rounded-xl shadow-sm"
                            />
                        ) : (
                            <div className="w-9 h-9 bg-violet-100 text-violet-600 rounded-xl flex items-center justify-center font-bold shadow-sm">
                                {user.displayName.charAt(0)}
                            </div>
                        )}
                        <div className="hidden sm:block pr-4">
                            <div className="flex items-center gap-2 mb-0.5">
                                <p className="text-sm font-semibold text-slate-800 line-clamp-1 leading-none">{user.displayName}</p>
                                {user.role && (
                                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                                        user.role.toLowerCase() === 'admin' 
                                            ? 'bg-violet-100 text-violet-600 border-violet-200' 
                                            : 'bg-slate-100 text-slate-500 border-slate-200'
                                    }`}>
                                        {user.role}
                                    </span>
                                )}
                            </div>
                            <p className="text-[10px] text-slate-400 font-medium leading-none">{user.email}</p>
                        </div>
                    </div>
                )}

                <button
                    onClick={handleLogout}
                    className="p-3 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all active:scale-95 group"
                    title="Logout"
                >
                    <LogOut size={22} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
            </div>
        </header>
    );
}
